import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';
import { Input, Select, Label } from '../components/ui/Input';
import { SECTIONS, TAB_STATUS } from '../components/Layout';

// #4a — Governance UI for the typed commercial system-of-record (#4).
// Two surfaces, both backed by endpoints that already exist:
//   • Access control  → GET /api/promo-roles, PUT /api/promo-role/{oid}  (Admin only)
//   • Audit trail     → GET /api/commercial-audit                        (any signed-in user)
//   • Sidebar tabs    → GET/PUT /api/app-tabs                             (069, any editor)

const ROLES = ['Admin', 'SalesEditor', 'MarketingEditor', 'Viewer'];

const ROLE_HELP = {
  Admin: 'Full control — manages roles and edits everything.',
  SalesEditor: 'Editor (Sales emphasis). Edits commercial data.',
  MarketingEditor: 'Editor (Marketing emphasis). Edits commercial data.',
  Viewer: 'Read-only — blocked from writing any commercial system-of-record data.',
};

const ROLE_COLOR = {
  Admin: 'var(--ai)',
  SalesEditor: 'var(--info)',
  MarketingEditor: 'var(--info)',
  Viewer: 'var(--text-muted)',
};

function fmt(s) {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Badge({ role }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      color: ROLE_COLOR[role] ?? 'var(--text-muted)', border: `1px solid ${ROLE_COLOR[role] ?? 'var(--border-soft)'}`,
    }}>{role}</span>
  );
}

const TH = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)' };
const TD = { padding: '8px 10px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-soft)' };

/*
 * Sidebar tab control (migration 069) — Matthew, 18 Sep:
 *
 *   "A number of the tabs on the left are an idea in trial and not in use. Can
 *    you have a system where we only show the ones in use ... have the ability
 *    to flag as not active or still building. Would be nice to have control
 *    over this to change."
 *
 * It lives on Governance because that is where the app's own settings already
 * are, and because it is an editor-level control rather than an Admin one:
 * requireCommercialWrite is default-ALLOW, so anyone who can edit commercial
 * data can tidy the sidebar, and every change lands in the audit table below.
 */
function TabControl({ apiFetch, qc, card }) {
  const { data: overrides = [], isLoading } = useQuery({
    queryKey: ['app-tabs'],
    queryFn: async () => {
      const r = await apiFetch('/app-tabs');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    staleTime: 60_000,
  });
  const [err, setErr] = useState('');
  const statusOf = useMemo(() => {
    const m = {};
    for (const row of overrides) m[row.tabId] = row.status;
    return m;
  }, [overrides]);

  const setStatus = useMutation({
    mutationFn: async ({ tabId, status }) => {
      const r = await apiFetch('/app-tabs', { method: 'PUT', body: JSON.stringify({ tabId, status }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['app-tabs'] }); },
    onError: (e) => setErr(String(e?.message || e)),
  });

  const counts = useMemo(() => {
    const all = SECTIONS.flatMap((s) => s.tabs);
    const c = { live: 0, preview: 0, building: 0, hidden: 0 };
    for (const t of all) c[statusOf[t.id] ?? 'live'] += 1;
    return c;
  }, [statusOf]);

  return (
    <div style={card}>
      <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Sidebar tabs</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        What each page in the sidebar is for. <strong style={{ color: 'var(--text-primary)' }}>Hidden</strong> takes
        it off the sidebar for everyone; <strong style={{ color: 'var(--text-primary)' }}>Building</strong> and{' '}
        <strong style={{ color: 'var(--text-primary)' }}>Preview</strong> leave it there with a badge so people know
        not to rely on it yet. Nothing is deleted and no data is touched — a hidden page comes straight back.
        {' '}Currently {counts.live} live, {counts.preview} preview, {counts.building} building, {counts.hidden} hidden.
      </div>
      {err && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 'var(--radius)',
          color: 'var(--danger)', padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{err}</div>
      )}
      {isLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
      {!isLoading && SECTIONS.map((section) => (
        <div key={section.label} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)',
            fontWeight: 600, marginBottom: 6 }}>{section.label}</div>
          {section.tabs.map((tab) => {
            const st = statusOf[tab.id] ?? 'live';
            return (
              <div key={tab.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0',
                borderBottom: '1px solid var(--border-soft)' }}>
                <span style={{ flex: 1, fontSize: 13, color: st === 'hidden' ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                  {tab.label}
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {Object.entries(TAB_STATUS).map(([key, meta]) => (
                    <button
                      key={key}
                      onClick={() => setStatus.mutate({ tabId: tab.id, status: key })}
                      disabled={setStatus.isPending}
                      title={meta.hint}
                      style={{
                        background: st === key ? 'var(--info-soft)' : 'transparent',
                        border: `1px solid ${st === key ? 'var(--info-border)' : 'var(--border-soft)'}`,
                        borderRadius: 999, color: st === key ? 'var(--info)' : 'var(--text-muted)',
                        padding: '2px 10px', fontSize: 11, cursor: setStatus.isPending ? 'wait' : 'pointer',
                        fontWeight: st === key ? 600 : 400,
                      }}
                    >
                      {meta.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function Governance() {
  const apiFetch = useApi();
  const qc = useQueryClient();

  // ── Roles. The roster is only populated server-side when the caller is Admin. ──
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['promo-roles'],
    queryFn: () => apiFetch('/promo-roles').then((r) => r.json()),
    staleTime: 60_000,
  });
  const myRole = rolesData?.myRole ?? 'Viewer';
  const isAdmin = myRole === 'Admin';
  const roster = rolesData?.roles ?? [];

  // ── Audit trail (readable by any signed-in user). Pull a generous window and
  //    filter client-side so the entity dropdown stays stable. ──
  const { data: audit = [], isLoading: auditLoading } = useQuery({
    queryKey: ['commercial-audit'],
    queryFn: () => apiFetch('/commercial-audit?limit=500').then((r) => r.json()),
    staleTime: 30_000,
  });

  const [entityFilter, setEntityFilter] = useState('All');
  const entityOptions = useMemo(
    () => ['All', ...Array.from(new Set((audit || []).map((a) => a.entity).filter(Boolean))).sort()],
    [audit],
  );
  const filteredAudit = entityFilter === 'All' ? audit : audit.filter((a) => a.entity === entityFilter);

  // Real users discovered from the audit trail (genuine oids — never fabricated),
  // minus anyone who already has a role row. Lets an Admin restrict an active
  // user to Viewer without having to look their oid up by hand.
  const knownUsers = useMemo(() => {
    const seen = new Map();
    for (const a of audit || []) {
      if (a.changedByOid && !seen.has(a.changedByOid)) seen.set(a.changedByOid, a.changedByName || a.changedByOid);
    }
    const have = new Set(roster.map((r) => r.userOid));
    return [...seen.entries()].filter(([oid]) => !have.has(oid)).map(([oid, name]) => ({ oid, name }));
  }, [audit, roster]);

  // ── Assign / change role ──
  const [oid, setOid] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Viewer');
  const [err, setErr] = useState('');

  const assign = useMutation({
    mutationFn: (body) =>
      apiFetch(`/promo-role/${encodeURIComponent(body.oid)}`, {
        method: 'PUT',
        body: JSON.stringify({ role: body.role, email: body.email || undefined }),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || `HTTP ${r.status}`);
        return r.json();
      }),
    onSuccess: () => {
      setErr(''); setOid(''); setEmail('');
      qc.invalidateQueries({ queryKey: ['promo-roles'] });
    },
    onError: (e) => setErr(String(e?.message || e)),
  });

  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      {/* Model explainer + caller's role */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Any signed-in user may edit commercial data. Governance is the ability to
          restrict a user to <strong style={{ color: 'var(--text-primary)' }}>Viewer</strong> (read-only) and the
          permanent audit trail of every write. Admins manage roles below.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Your access</span>
          <Badge role={myRole} />
        </div>
      </div>

      {/* ── Access control ── */}
      <div style={card}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Access control</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          Roles live in <code>commercial.PromoUserRole</code>. The first Admin is bootstrapped by email allowlist.
        </div>

        {!isAdmin ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', border: '1px solid var(--border-soft)' }}>
            {rolesLoading ? 'Loading…' : 'Role administration is restricted to Admins. You can review the audit trail below.'}
          </div>
        ) : (
          <>
            {/* Assign / change a user's role */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
              <div style={{ flex: '2 1 280px', minWidth: 240 }}>
                <Label>User OID {knownUsers.length > 0 && <span style={{ textTransform: 'none', fontWeight: 400 }}>(pick a recent editor or paste an oid)</span>}</Label>
                <Input list="known-users" value={oid} onChange={(e) => setOid(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
                <datalist id="known-users">
                  {knownUsers.map((u) => <option key={u.oid} value={u.oid}>{u.name}</option>)}
                </datalist>
              </div>
              <div style={{ flex: '2 1 220px', minWidth: 180 }}>
                <Label>Email <span style={{ textTransform: 'none', fontWeight: 400 }}>(optional)</span></Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@majans.com" />
              </div>
              <div style={{ flex: '1 1 140px', minWidth: 130 }}>
                <Label>Role</Label>
                <Select value={role} onChange={(e) => setRole(e.target.value)}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </Select>
              </div>
              <Button
                variant="primary"
                disabled={!oid.trim() || assign.isPending}
                onClick={() => assign.mutate({ oid: oid.trim(), email: email.trim(), role })}
              >
                {assign.isPending ? 'Saving…' : 'Assign role'}
              </Button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>{ROLE_HELP[role]}</div>
            {err && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 16 }}>⚠ {err}</div>}

            {/* Current roster */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH}>Email</th>
                    <th style={TH}>Role</th>
                    <th style={TH}>Assigned by</th>
                    <th style={TH}>Assigned at</th>
                  </tr>
                </thead>
                <tbody>
                  {roster.length === 0 && (
                    <tr><td style={{ ...TD, color: 'var(--text-muted)' }} colSpan={4}>No explicit role assignments yet — everyone is a default editor.</td></tr>
                  )}
                  {roster.map((u) => (
                    <tr key={u.userOid}>
                      <td style={TD}>{u.email || <span style={{ color: 'var(--text-muted)' }}>{u.userOid}</span>}</td>
                      <td style={TD}>
                        <Select
                          value={u.role}
                          style={{ width: 160 }}
                          disabled={assign.isPending}
                          onChange={(e) => assign.mutate({ oid: u.userOid, email: u.email || '', role: e.target.value })}
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </Select>
                      </td>
                      <td style={{ ...TD, color: 'var(--text-muted)' }}>{u.assignedBy || '—'}</td>
                      <td style={{ ...TD, color: 'var(--text-muted)' }}>{fmt(u.assignedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Sidebar tabs (069) ── */}
      <TabControl apiFetch={apiFetch} qc={qc} card={card} />

      {/* ── Audit trail ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Audit trail</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Append-only record of every write to the typed system-of-record. Most recent 500.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Entity</span>
            <Select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} style={{ width: 200 }}>
              {entityOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </Select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filteredAudit.length} entries</span>
          </div>
        </div>

        <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...TH, position: 'sticky', top: 0, background: 'var(--bg-card)' }}>When</th>
                <th style={{ ...TH, position: 'sticky', top: 0, background: 'var(--bg-card)' }}>Entity</th>
                <th style={{ ...TH, position: 'sticky', top: 0, background: 'var(--bg-card)' }}>ID</th>
                <th style={{ ...TH, position: 'sticky', top: 0, background: 'var(--bg-card)' }}>Action</th>
                <th style={{ ...TH, position: 'sticky', top: 0, background: 'var(--bg-card)' }}>By</th>
              </tr>
            </thead>
            <tbody>
              {auditLoading && (
                <tr><td style={{ ...TD, color: 'var(--text-muted)' }} colSpan={5}>Loading…</td></tr>
              )}
              {!auditLoading && filteredAudit.length === 0 && (
                <tr><td style={{ ...TD, color: 'var(--text-muted)' }} colSpan={5}>No audit entries{entityFilter !== 'All' ? ` for ${entityFilter}` : ''} yet.</td></tr>
              )}
              {filteredAudit.map((a) => (
                <tr key={a.auditId}>
                  <td style={{ ...TD, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{fmt(a.changedAt)}</td>
                  <td style={TD}>{a.entity}</td>
                  <td style={{ ...TD, color: 'var(--text-muted)' }}>{a.entityId ?? '—'}</td>
                  <td style={TD}>
                    <span style={{ color: a.action === 'delete' ? 'var(--danger)' : 'var(--success)', fontWeight: 500 }}>{a.action}</span>
                  </td>
                  <td style={TD}>{a.changedByName || <span style={{ color: 'var(--text-muted)' }}>{a.changedByOid}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
